import React, { useEffect, useState } from 'react';
import { useAuth } from '../../security/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, FormControl } from '@mui/material';
import Link from '@mui/material/Link';
import Spinner from '../../ui/Spinner';
import { Checkbox } from '../../components/Checkbox';
import { Button } from '../../components/Button';
import { AuthFormContainer } from '../../components/AuthFormContainer';
import { ErrorAlert } from '../../components/ErrorAlert';
import { TextField } from '../../components/TextField';
import { resendAccountSetupRequest } from '../../api/AuthApi';
import {AuthErrorCode, getErrorMessage} from "../../api/types";

const Login: React.FC = () => {

    const { userAccount, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [stayLoggedIn, setStayLoggedIn] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [processingLogin, setProcessingLogin] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [errorCode, setErrorCode] = useState<AuthErrorCode | null>(null);

    const from = location.state?.from?.pathname || '/';

    useEffect(() => {
        if (userAccount) {
            navigate(from, { replace: true });
        }
    }, [userAccount, navigate, from]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setErrorCode(null);
        setProcessingLogin(true);

        try {
            const result = await login({
                username: email,
                password,
                stayLoggedIn
            });

            if (result.success) {
                // Success - navigation handled by useEffect
                setError(null);
                setErrorCode(null);
            } else {
                const code = result.errorCode || AuthErrorCode.UNEXPECTED_ERROR;
                setErrorCode(code);
                setError(getErrorMessage(code));
            }
        } catch (error) {
            console.error('Login error:', error);
            setErrorCode(AuthErrorCode.UNEXPECTED_ERROR);
            setError(getErrorMessage(AuthErrorCode.UNEXPECTED_ERROR));
        } finally {
            setProcessingLogin(false);
        }
    };

    const handleForgotPassword = (e: React.MouseEvent) => {
        e.preventDefault();
        navigate('/forgot-password', { replace: true });
    };

    const handleResendAccountSetup = async () => {
        if (!email) return;
        setLoading(true);

        try {
            await resendAccountSetupRequest({ username: email });
        } catch (err) {
            console.error('Failed to resend account setup:', err);
        } finally {
            setLoading(false);
        }
    };

    const showResendLink = errorCode === AuthErrorCode.PENDING_USER_ACCOUNT_SETUP;
    const loginButtonDisabled = !email.trim() || !password.trim() || processingLogin;

    if (loading) return <Spinner />;

    return (
        <AuthFormContainer title="Anmelden">
            {error && (
                <ErrorAlert
                    message={error}
                    action={
                        showResendLink
                            ? {
                                label: 'Klicken Sie hier, um den Link erneut zu versenden',
                                onClick: handleResendAccountSetup
                            }
                            : undefined
                    }
                />
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
                <FormControl fullWidth required>
                    <TextField
                        autoFocus
                        id="email"
                        name="email"
                        placeholder="E-Mail-Adresse"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e)}
                    />
                </FormControl>

                <FormControl fullWidth required sx={{ mt: 2 }}>
                    <TextField
                        id="password"
                        name="password"
                        placeholder="Passwort"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e)}
                    />
                </FormControl>

                <Checkbox
                    id="stayLoggedIn"
                    name="stayLoggedIn"
                    className="mt-4"
                    isSelected={stayLoggedIn}
                    onChange={setStayLoggedIn}
                >
                    Angemeldet bleiben?
                </Checkbox>

                <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="mt-4"
                    loading={processingLogin}
                    isDisabled={loginButtonDisabled}
                >
                    Anmelden
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={handleForgotPassword}
                        sx={{ cursor: 'pointer' }}
                    >
                        Passwort vergessen?
                    </Link>
                </Box>
            </Box>
        </AuthFormContainer>
    );
};

export default Login;